import { DateTime } from 'luxon';
import { throwError } from 'rxjs';
import { WorkflowAssigneeType } from './workflow.types';
import { trySetProp, uuid } from '../global.functions';
import { CompareDefinition, ComparisonType, LogicalAndTruthCondition } from './truth-condition.model';
import { HttpRequestMethodType, MappedField, ServiceDataFormat } from './common.model';


/** Enumeration of events that may trigger workflow.
 *  WARNING: DO NOT change the order of these items (they render as they are defined)
 */
export enum TriggerEventType {
    Submit = "Submit",
    AtAnyTime = "AtAnyTime",
    OnDateTime = "OnDateTime",
    AfterDateTime = "AfterDateTime",
    EmailLink = "EmailLink",
    DocuSignEvent = "DocuSignEvent",
    ApplicationCreated = "ApplicationCreated",
}


export class RecipientList {
    users: string[] = [];
    roles: string[] = [];
    formFields: string[] = [];
    staticEmails: string[] = [];

    constructor(data?: any) {
    }

    init(_data?: any) {
        if (!_data) {
            return;
        }

        if (Array.isArray(_data["Users"])) {
            this.users = [] as any;
            for (let item of _data["Users"])
                this.users!.push(item);
        }
        if (Array.isArray(_data["Roles"])) {
            this.roles = [] as any;
            for (let item of _data["Roles"])
                this.roles!.push(item);
        }
        if (Array.isArray(_data["FormFields"])) {
            this.formFields = [] as any;
            for (let item of _data["FormFields"])
                this.formFields!.push(item);
        }
        if (Array.isArray(_data["StaticEmails"])) {
            this.staticEmails = [] as any;
            for (let item of _data["StaticEmails"])
                this.staticEmails!.push(item);
        }
    }

    static fromJS(data: any): RecipientList {
        data = typeof data === 'object' ? data : {};
        let result = new RecipientList();
        result.init(data);
        return result;
    }

    toJSON(data?: any) {
        data = typeof data === 'object' ? data : {};

        if (Array.isArray(this.users)) {
            data["Users"] = [];
            for (let item of this.users)
                data["Users"].push(item);
        }
        if (Array.isArray(this.roles)) {
            data["Roles"] = [];
            for (let item of this.roles)
                data["Roles"].push(item);
        }
        if (Array.isArray(this.formFields)) {
            data["FormFields"] = [];
            for (let item of this.formFields)
                data["FormFields"].push(item);
        }
        if (Array.isArray(this.staticEmails)) {
            data["StaticEmails"] = [];
            for (let item of this.staticEmails)
                data["StaticEmails"].push(item);
        }

        return data;
    }
}

export interface IItemState
{
    guid: string | undefined;
    name: string | undefined;
}

/**
 * An object representing a state of an item or workflow.
 * These objects are a simple referenc eto the GUID and name of the state.
 */
export class ItemState implements IItemState
{
    guid: string | undefined = uuid();
    name!: string | undefined;

    /** Initializes a new instance of the "ItemState" class.
     * @guid The state identifier.
     * @name The item state name. */
    constructor(data?: IItemState) {
        if (data) {
            for (let property in data) {
                if (data.hasOwnProperty(property))
                    (<any>this)[property] = (<any>data)[property];
            }
        }
    }

    init(_data?: any) {
        if (_data) {
            trySetProp(this, _data, "guid");
            trySetProp(this, _data, "name");
        }
    }

    static fromJS(data: any): ItemState {
        data = typeof data === 'object' ? data : {};
        let result = new ItemState();
        result.init(data);
        return result;
    }

    toJSON(data?: any) {
        data = typeof data === 'object' ? data : {};
        data["guid"] = this.guid;
        data["name"] = this.name;
        return data;
    }
}

/*
WorkflowTransitionActionType: Email, WebHook, DocuSign, AppCreation = "AppCreation",
*/

export class WorkflowActionFactory {
    static createAction(type: string): WorkflowTransitionAction {
        switch (type) {
            case WorkflowTransitionActionType.Email:
                return new EmailWorkflowTransitionAction();

            case WorkflowTransitionActionType.WebHook:
                return new WebHookWorkflowTransitionAction();

            case WorkflowTransitionActionType.DocuSign:
                return new DocuSignWorkflowTransitionAction();

            case WorkflowTransitionActionType.AppCreation:
                return new AppCreationWorkflowTransitionAction();

            default:
                throwError(() => new Error('Invalid workflow action type: ' + type));
        }
    }
}

/** The link to workflow action. */
export class WorkflowActionLink implements IWorkflowActionLink {
    /** Gets or sets the workflow action name. */
    actionName!: string | undefined;
    /** Gets or sets the workflow transition name. */
    transitionName!: string | undefined;
    /** Gets or sets the workflow state name. */
    stateName!: string | undefined;

    constructor(data?: IWorkflowActionLink) {
        if (data) {
            for (let property in data) {
                if (data.hasOwnProperty(property))
                    (<any>this)[property] = (<any>data)[property];
            }
        }
    }

    init(_data?: any) {
        if (_data) {
            this.actionName = _data["actionName"];
            this.transitionName = _data["transitionName"];
            this.stateName = _data["stateName"];
        }
    }

    static fromJS(data: any): WorkflowActionLink {
        data = typeof data === 'object' ? data : {};
        let result = new WorkflowActionLink();
        result.init(data);
        return result;
    }

    toJSON(data?: any) {
        data = typeof data === 'object' ? data : {};
        data["actionName"] = this.actionName;
        data["transitionName"] = this.transitionName;
        data["stateName"] = this.stateName;
        return data;
    }
}

/** The link to workflow action. */
export interface IWorkflowActionLink {
    /** Gets or sets the workflow action name. */
    actionName: string | undefined;
    /** Gets or sets the workflow transition name. */
    transitionName: string | undefined;
    /** Gets or sets the workflow state name. */
    stateName: string | undefined;
}

/** Represents workflow configuration. */
export class WorkflowConfiguration implements IWorkflowConfiguration {
    /** Gets or sets the list of valid iApply.Model.Workflow.WorkflowStates. */
    states!: WorkflowState[] | undefined;

    constructor(data?: IWorkflowConfiguration) {
        if (data) {
            for (let property in data) {
                if (data.hasOwnProperty(property))
                    (<any>this)[property] = (<any>data)[property];
            }
        }
    }

    init(_data?: any) {
        if (_data) {
            if (Array.isArray(_data["states"])) {
                this.states = [] as any;
                for (let item of _data["states"])
                    this.states!.push(WorkflowState.fromJS(item));
            }
        }
    }

    static fromJS(data: any): WorkflowConfiguration {
        data = typeof data === 'object' ? data : {};
        let result = new WorkflowConfiguration();
        result.init(data);
        return result;
    }

    toJSON(data?: any) {
        data = typeof data === 'object' ? data : {};
        if (Array.isArray(this.states)) {
            data["states"] = [];
            for (let item of this.states)
                data["states"].push(item.toJSON());
        }
        return data;
    }
}

/** Represents workflow configuration. */
export interface IWorkflowConfiguration {
    /** Gets or sets the list of valid iApply.Model.Workflow.WorkflowStates. */
    states: WorkflowState[] | undefined;
}

/** A container for a iApply.Model.Workflow.WorkflowConfigurationContainer object. */
export class WorkflowConfigurationContainer implements IWorkflowConfigurationContainer {
    type!: WorkflowTargetType;
    /** Gets or sets the target id. */
    externalId!: string | undefined;
    /** Gets or sets the form version this security configuration is tied to. */
    externalVersion!: number;
    configuration!: WorkflowConfiguration;
    id!: string | undefined;

    constructor(data?: IWorkflowConfigurationContainer) {
        if (data) {
            for (let property in data) {
                if (data.hasOwnProperty(property))
                    (<any>this)[property] = (<any>data)[property];
            }
        }
    }

    init(_data?: any) {
        if (_data) {
            this.type = _data["type"];
            this.externalId = _data["externalId"];
            this.externalVersion = _data["externalVersion"];
            this.configuration = _data["configuration"] ? WorkflowConfiguration.fromJS(_data["configuration"]) : <any>undefined;
            this.id = _data["id"];
        }
    }

    static fromJS(data: any): WorkflowConfigurationContainer {
        data = typeof data === 'object' ? data : {};
        let result = new WorkflowConfigurationContainer();
        result.init(data);
        return result;
    }

    toJSON(data?: any) {
        data = typeof data === 'object' ? data : {};
        data["type"] = this.type;
        data["externalId"] = this.externalId;
        data["externalVersion"] = this.externalVersion;
        data["configuration"] = this.configuration ? this.configuration.toJSON() : <any>undefined;
        data["id"] = this.id;
        return data;
    }
}

/** A container for a iApply.Model.Workflow.WorkflowConfigurationContainer object. */
export interface IWorkflowConfigurationContainer {
    type: WorkflowTargetType;
    /** Gets or sets the target id. */
    externalId: string | undefined;
    /** Gets or sets the form version this security configuration is tied to. */
    externalVersion: number;
    configuration: WorkflowConfiguration;
    id: string | undefined;
}

/** Represents a workflow state. */
export class WorkflowState implements IWorkflowState {
    /** Gets or sets the name of the state. */
    name!: string | undefined;
    /** Gets or sets the list of valid iApply.Model.Workflow.WorkflowTransitions for the state. */
    transitions!: WorkflowTransition[] | undefined;
    /** Gets or sets the GUID identifier. */
    guid!: string;

    constructor(data?: IWorkflowState) {
        if (data) {
            for (let property in data) {
                if (data.hasOwnProperty(property))
                    (<any>this)[property] = (<any>data)[property];
            }
        }
    }

    init(_data?: any) {
        if (_data) {
            this.name = _data["name"];
            if (Array.isArray(_data["transitions"])) {
                this.transitions = [] as any;
                for (let item of _data["transitions"])
                    this.transitions!.push(WorkflowTransition.fromJS(item));
            }
            this.guid = _data["guid"];
        }
    }

    static fromJS(data: any): WorkflowState {
        data = typeof data === 'object' ? data : {};
        let result = new WorkflowState();
        result.init(data);
        return result;
    }

    toJSON(data?: any) {
        data = typeof data === 'object' ? data : {};
        data["name"] = this.name;
        if (Array.isArray(this.transitions)) {
            data["transitions"] = [];
            for (let item of this.transitions)
                data["transitions"].push(item.toJSON());
        }
        data["guid"] = this.guid;
        return data;
    }
}

/** Represents a workflow state. */
export interface IWorkflowState {
    /** Gets or sets the name of the state. */
    name: string | undefined;
    /** Gets or sets the list of valid iApply.Model.Workflow.WorkflowTransitions for the state. */
    transitions: WorkflowTransition[] | undefined;
    /** Gets or sets the GUID identifier. */
    guid: string;
}

/** Workflow target object type enumeration. */
export enum WorkflowTargetType {
    FormApplication = "FormApplication",
    ProductDefinition = "ProductDefinition",
    Unknown = "Unknown",
}

/** Represents a workflow transition. */
export class WorkflowTransition implements IWorkflowTransition {
    /** Gets or sets the GUID identifier. */
    guid!: string;
    /** Gets or sets the parent State identifier. */
    parentId!: string;
    /** Gets or sets the transition name. */
    name!: string | undefined;
    trigger: TriggerEventType = TriggerEventType.Submit;
    /** Gets or sets the name of the exit state. */
    exitStateName!: string | undefined;
    /** Gets or sets a value indicating whether drafts applications are allowed to be transitioned in Workflow. */
    allowDraftApplication: boolean = false;
    /** Gets or sets the exit assignee, which may refer to a user, a role, or a form field. */
    exitAssignee: string[] = [];
    exitAssigneeType: WorkflowAssigneeType = WorkflowAssigneeType.Unchanged;
    exitAssigneeSource!: ComparisonType;
    exitOrganisation!: CompareDefinition;
    condition: LogicalAndTruthCondition;
    onDateTimeCompare: ComparisonType;
    /** Gets or sets the on date / time value. */
    onDateTimeValue!: DateTime | undefined;
    /** Gets or sets the on date / time field. */
    onDateTimeField!: string | undefined;
    /** Gets or sets the on date / time offset. */
    onDateTimeOffset!: string | undefined;
    /** Gets or sets the after date / time value. */
    afterDateTime!: string | undefined;
    onDocuSignEvent!: WorkflowActionLink;
    onAppCreation!: WorkflowActionLink;
    /** Gets or sets the Workflow State Name from the application created by Application Creation action,
that triggers this transition. */
    onAppCreationWorkflowState!: string | undefined;
    /** Gets or sets the list of transition actions. */
    events: WorkflowTransitionAction[] = [];
    /** Gets or sets value indicating whether this transition should allow
Concurrent Repeater editing with Workflow synchronization. */
    allowConcurrentEditing!: boolean | undefined;
    /** Gets or sets the workflow transition iteration counter. */
    repeatCounter: number = 1;
    repeaterCondition!: LogicalAndTruthCondition;

    constructor(data?: IWorkflowTransition) {
        if (data) {
            for (let property in data) {
                if (data.hasOwnProperty(property))
                    (<any>this)[property] = (<any>data)[property];
            }
        }
    }

    init(_data?: any) {
        if (_data) {
            this.parentId = _data["parentId"];
            this.name = _data["name"];
            this.trigger = _data["trigger"];
            this.exitStateName = _data["exitStateName"];
            this.allowDraftApplication = _data["allowDraftApplication"];
            if (Array.isArray(_data["exitAssignee"])) {
                this.exitAssignee = [] as any;
                for (let item of _data["exitAssignee"])
                    this.exitAssignee!.push(item);
            }
            this.exitAssigneeType = _data["exitAssigneeType"];
            this.exitOrganisation = _data["exitOrganisation"] ? CompareDefinition.fromJS(_data["exitOrganisation"]) : <any>undefined;
            this.condition = _data["condition"] ? LogicalAndTruthCondition.fromJS(_data["condition"]) : <any>undefined;
            this.onDateTimeCompare = _data["onDateTimeCompare"];
            this.onDateTimeValue = _data["onDateTimeValue"] ? DateTime.fromISO(_data["onDateTimeValue"].toString()) : <any>undefined;
            this.onDateTimeField = _data["onDateTimeField"];
            this.onDateTimeOffset = _data["onDateTimeOffset"];
            this.afterDateTime = _data["afterDateTime"];
            this.onDocuSignEvent = _data["onDocuSignEvent"] ? WorkflowActionLink.fromJS(_data["onDocuSignEvent"]) : <any>undefined;
            this.onAppCreation = _data["onAppCreation"] ? WorkflowActionLink.fromJS(_data["onAppCreation"]) : <any>undefined;
            this.onAppCreationWorkflowState = _data["onAppCreationWorkflowState"];
            if (Array.isArray(_data["events"])) {
                this.events = [] as any;
                for (let item of _data["events"])
                    this.events!.push(WorkflowTransitionAction.fromJS(item));
            }
            this.allowConcurrentEditing = _data["allowConcurrentEditing"];
            this.repeatCounter = _data["repeatCounter"];
            this.repeaterCondition = _data["repeaterCondition"] ? LogicalAndTruthCondition.fromJS(_data["repeaterCondition"]) : <any>undefined;
            this.guid = _data["guid"];
        }
    }

    static fromJS(data: any): WorkflowTransition {
        data = typeof data === 'object' ? data : {};
        let result = new WorkflowTransition();
        result.init(data);
        return result;
    }

    toJSON(data?: any) {
        data = typeof data === 'object' ? data : {};
        data["parentId"] = this.parentId;
        data["name"] = this.name;
        data["trigger"] = this.trigger;
        data["exitStateName"] = this.exitStateName;
        data["allowDraftApplication"] = this.allowDraftApplication;
        if (Array.isArray(this.exitAssignee)) {
            data["exitAssignee"] = [];
            for (let item of this.exitAssignee)
                data["exitAssignee"].push(item);
        }
        data["exitAssigneeType"] = this.exitAssigneeType;
        data["exitOrganisation"] = this.exitOrganisation ? this.exitOrganisation.toJSON() : <any>undefined;
        data["condition"] = this.condition ? this.condition.toJSON() : <any>undefined;
        data["onDateTimeCompare"] = this.onDateTimeCompare;
        data["onDateTimeValue"] = this.onDateTimeValue ? this.onDateTimeValue.toString() : <any>undefined;
        data["onDateTimeField"] = this.onDateTimeField;
        data["onDateTimeOffset"] = this.onDateTimeOffset;
        data["afterDateTime"] = this.afterDateTime;
        data["onDocuSignEvent"] = this.onDocuSignEvent ? this.onDocuSignEvent.toJSON() : <any>undefined;
        data["onAppCreation"] = this.onAppCreation ? this.onAppCreation.toJSON() : <any>undefined;
        data["onAppCreationWorkflowState"] = this.onAppCreationWorkflowState;
        if (Array.isArray(this.events)) {
            data["events"] = [];
            for (let item of this.events)
                data["events"].push(item.toJSON());
        }
        data["allowConcurrentEditing"] = this.allowConcurrentEditing;
        data["repeatCounter"] = this.repeatCounter;
        data["repeaterCondition"] = this.repeaterCondition ? this.repeaterCondition.toJSON() : <any>undefined;
        data["guid"] = this.guid;
        return data;
    }
}

/** Represents a workflow transition. */
export interface IWorkflowTransition {
    /** Gets or sets the parent State identifier. */
    parentId: string;
    /** Gets or sets the transition name. */
    name: string | undefined;
    trigger: TriggerEventType;
    /** Gets or sets the name of the exit state. */
    exitStateName: string | undefined;
    /** Gets or sets a value indicating whether drafts applications are allowed to be transitioned in Workflow. */
    allowDraftApplication: boolean;
    /** Gets or sets the exit assignee, which may refer to a user, a role, or a form field. */
    exitAssignee: string[] | undefined;
    exitAssigneeType: WorkflowAssigneeType;
    exitAssigneeSource: string | undefined;
    exitOrganisation: CompareDefinition;
    condition: LogicalAndTruthCondition;
    onDateTimeCompare: ComparisonType;
    /** Gets or sets the on date / time value. */
    onDateTimeValue: DateTime | undefined;
    /** Gets or sets the on date / time field. */
    onDateTimeField: string | undefined;
    /** Gets or sets the on date / time offset. */
    onDateTimeOffset: string | undefined;
    /** Gets or sets the after date / time value. */
    afterDateTime: string | undefined;
    onDocuSignEvent: WorkflowActionLink;
    onAppCreation: WorkflowActionLink;
    /** Gets or sets the Workflow State Name from the application created by Application Creation action,
that triggers this transition. */
    onAppCreationWorkflowState: string | undefined;
    /** Gets or sets the list of transition actions. */
    events: WorkflowTransitionAction[] | undefined;
    /** Gets or sets value indicating whether this transition should allow
Concurrent Repeater editing with Workflow synchronization. */
    allowConcurrentEditing: boolean | undefined;
    /** Gets or sets the workflow transition iteration counter. */
    repeatCounter: number | undefined;
    repeaterCondition: LogicalAndTruthCondition;
    /** Gets or sets the GUID identifier. */
    guid: string;
}

/** An interface for a workflow transition action. */
export class WorkflowTransitionAction implements IWorkflowTransitionAction {
    /** Gets or sets the name of the transition action. */
    name!: string | undefined;
    type!: WorkflowTransitionActionType;
    condition!: LogicalAndTruthCondition;

    constructor(data?: IWorkflowTransitionAction) {
        if (data) {
            for (let property in data) {
                if (data.hasOwnProperty(property))
                    (<any>this)[property] = (<any>data)[property];
            }
        }
    }

    init(_data?: any) {
        if (_data) {
            this.name = _data["name"];
            this.type = _data["type"];
            this.condition = _data["condition"] ? LogicalAndTruthCondition.fromJS(_data["condition"]) : <any>undefined;
        }
    }

    static fromJS(data: any): WorkflowTransitionAction {
        data = typeof data === 'object' ? data : {};
        let result = new WorkflowTransitionAction();
        result.init(data);
        return result;
    }

    toJSON(data?: any) {
        data = typeof data === 'object' ? data : {};
        data["name"] = this.name;
        data["type"] = this.type;
        data["condition"] = this.condition ? this.condition.toJSON() : <any>undefined;
        return data;
    }
}

/** An interface for a workflow transition action. */
export interface IWorkflowTransitionAction {
    /** Gets or sets the name of the transition action. */
    name: string | undefined;
    type: WorkflowTransitionActionType;
    condition: LogicalAndTruthCondition;
}

/** An enumeration of iApply.Model.Workflow.Event.WorkflowTransitionAction types. */
export enum WorkflowTransitionActionType {
    Email = "Email",
    WebHook = "WebHook",
    DocuSign = "DocuSign",
    AppCreation = "AppCreation",
}

// CUSTOM UI CLASSES NOT EXPORTED

export interface IServiceEndpointUtilisation
{
    /**  Gets or sets the id of the <see cref="ServiceEndpoint"/>. */
    serviceEndpointId: string;
    /**  Gets or sets the HTTP method on REST web services. */
    restHttpMethod: HttpRequestMethodType | undefined;
    /**  Gets or sets the request format on REST web services. */
    restRequestFormat: ServiceDataFormat | undefined;
    /**  Gets or sets the name of the root container element for REST web services. */
    restRequestRoot: string;
    /**  Gets or sets the list of mapped fields for the request. */
    requestFieldMap: MappedField[];
    /**  Gets or sets the list of mapped fields for the response. */
    responseFieldMap: MappedField[];
    /**  Gets or sets the maximum number of results to return. */
    maxResults: number | undefined;
    /**  Gets or sets a value indicating whether to [map all iApply fields to identical named target fields] in request. */
    mapAllFieldsInRequest: boolean;
    /**  Gets or sets a value indicating whether to [map all iApply metadata fields to "Metadata_*" named target fields] in request. */
    mapAllMetadataFieldsInRequest: boolean;
    /**  Gets or sets a value indicating whether [response fields should match request fields]. */
    responseFieldsMatchRequest: boolean;
    /**  Gets or sets associated XML transformation scenario. Used only for XML web service. */
    transformName: string;
}

export class ServiceEndpointUtilisation implements IServiceEndpointUtilisation
{
    /**  Gets or sets the id of the <see cref="ServiceEndpoint"/>. */
    serviceEndpointId!: string;
    /**  Gets or sets the HTTP method on REST web services. */
    restHttpMethod: HttpRequestMethodType = HttpRequestMethodType.GET;
    /**  Gets or sets the request format on REST web services. */
    restRequestFormat: ServiceDataFormat = ServiceDataFormat.Json;
    /**  Gets or sets the name of the root container element for REST web services. */
    restRequestRoot: string | undefined = '';
    /**  Gets or sets the list of mapped fields for the request. */
    requestFieldMap: MappedField[] = [];
    /**  Gets or sets the list of mapped fields for the response. */
    responseFieldMap: MappedField[] = [];
    /**  Gets or sets the maximum number of results to return. */
    maxResults: number | undefined = 20;
    /**  Gets or sets a value indicating whether to [map all iApply fields to identical named target fields] in request. */
    mapAllFieldsInRequest: boolean = false;
    /**  Gets or sets a value indicating whether to [map all iApply metadata fields to "Metadata_*" named target fields] in request. */
    mapAllMetadataFieldsInRequest: boolean = false;
    /**  Gets or sets a value indicating whether [response fields should match request fields]. */
    responseFieldsMatchRequest: boolean = false;
    /**  Gets or sets associated XML transformation scenario. Used only for XML web service. */
    transformName: string | undefined = null;

    constructor(data?: IServiceEndpointUtilisation) {
        if (data) {
            for (let property in data) {
                if (data.hasOwnProperty(property))
                    (<any>this)[property] = (<any>data)[property];
            }
        }
    }

    init(_data?: any) {
        if (_data) {
            this.serviceEndpointId = _data["ServiceEndpointId"];
            this.restHttpMethod = _data["RestHttpMethod"];
            this.restRequestFormat = _data["RestRequestFormat"];
            this.restRequestRoot = _data["RestRequestRoot"];
            this.requestFieldMap = [] as any;
            if (Array.isArray(_data["RequestFieldMap"])) {
                for (let item of _data["RequestFieldMap"])
                    this.requestFieldMap.push(MappedField.fromJS(item));
            }
            this.responseFieldMap = [] as any;
            if (Array.isArray(_data["ResponseFieldMap"])) {
                for (let item of _data["ResponseFieldMap"])
                    this.responseFieldMap.push(MappedField.fromJS(item));
            }
            this.maxResults = _data["MaxResults"];
            this.mapAllFieldsInRequest = _data["MapAllFieldsInRequest"];
            this.mapAllMetadataFieldsInRequest = _data["MapAllMetadataFieldsInRequest"];
            this.responseFieldsMatchRequest = _data["ResponseFieldsMatchRequest"];
            this.transformName = _data["TransformName"];
        }
    }

    static fromJS(data: any): ServiceEndpointUtilisation {
        data = typeof data === 'object' ? data : {};
        let result = new ServiceEndpointUtilisation();
        result.init(data);
        return result;
    }

    toJSON(data?: any)
    {
        data = typeof data === 'object' ? data : {};
        data["serviceEndpointId"] = this.serviceEndpointId;
        data["restHttpMethod"] = this.restHttpMethod;
        data["restRequestFormat"] = this.restRequestFormat;
        data["restRequestRoot"] = this.restRequestRoot;

        data["requestFieldMap"] = [];
        if (Array.isArray(this.requestFieldMap)) {
            for (let item of this.requestFieldMap)
                data["requestFieldMap"].push(item.toJSON());
        }

        data["responseFieldMap"] = [];
        if (Array.isArray(this.responseFieldMap)) {
            for (let item of this.responseFieldMap)
                data["responseFieldMap"].push(item.toJSON());
        }

        data["maxResults"] = this.maxResults;
        data["mapAllFieldsInRequest"] = this.mapAllFieldsInRequest;
        data["mapAllMetadataFieldsInRequest"] = this.mapAllMetadataFieldsInRequest;
        data["responseFieldsMatchRequest"] = this.responseFieldsMatchRequest;
        data["transformName"] = this.transformName;
        return data;
    }
}

export class EmailWorkflowTransitionAction extends WorkflowTransitionAction
{
    type: WorkflowTransitionActionType = WorkflowTransitionActionType.Email;

    recipients: RecipientList = new RecipientList();
    content: string = '';
    subject: string = '';
    attachPdf: boolean = false;
    pdfRenderRole: string = '';
    emailRenderRole: string = '';
    emailLinks: string[] = [];
    attachUploadedFileControls: number[] = [];
    attachGeneratedDocumentsFromControl: number[] = [];
    generatedDocumentsRenderRole: string = '';
}

export class WebHookWorkflowTransitionAction extends ServiceEndpointUtilisation implements WorkflowTransitionAction
{
    name: string;
    type: WorkflowTransitionActionType;
    condition: LogicalAndTruthCondition;

    constructor(data?: IWorkflowTransitionAction | IServiceEndpointUtilisation)
    {
        super(<any>data);
    }

    init(_data?: any)
    {
        super.init(_data);
        if (_data) {
            this.name = _data["name"];
            this.type = _data["type"];
            this.condition = _data["condition"] ? LogicalAndTruthCondition.fromJS(_data["condition"]) : <any>undefined;
        }
    }

    static fromJS(data: any): WebHookWorkflowTransitionAction {
        data = typeof data === 'object' ? data : {};
        let result = new WebHookWorkflowTransitionAction();
        result.init(data);
        return result;
    }

    toJSON(data?: any)
    {
        data = super.toJSON(data);
        data["name"] = this.name;
        data["type"] = this.type;
        data["condition"] = this.condition ? this.condition.toJSON() : <any>undefined;
        return data;
    }
}

export class DocuSignWorkflowTransitionAction extends WorkflowTransitionAction
{
    type: WorkflowTransitionActionType = WorkflowTransitionActionType.DocuSign;
}

export class AppCreationWorkflowTransitionAction extends WorkflowTransitionAction
{
    type: WorkflowTransitionActionType = WorkflowTransitionActionType.AppCreation;

    formId: string;
    newApplicationOriginator: string;
    newApplicationOriginatorType: ComparisonType;
    applicationDataMapping: MappedField[] = [];
    mapAllFields: boolean;
    repeaterCondition: LogicalAndTruthCondition;
    emailRenderRole: string;
    emailContent: string;
    emailSubject: string;
    sendEmailToOriginator: boolean;
}

export interface IWorkflowHistoryItem {
    previousState: string;
    applicationState: string;
    author: string;
    date: DateTime;
    draft: boolean;
}

export class WorkflowHistoryItem implements IWorkflowHistoryItem {
    previousState!: string;
    applicationState!: string;
    author!: string;
    date!: DateTime;
    draft!: boolean;

    constructor(data?: IWorkflowHistoryItem) {
        if (data) {
            for (let property in data) {
                if (data.hasOwnProperty(property))
                    (<any>this)[property] = (<any>data)[property];
            }
        }
    }

    init(_data?: any) {
        if (_data) {
            trySetProp(this, _data, "previousState");
            trySetProp(this, _data, "applicationState");
            trySetProp(this, _data, "author");
            trySetProp(this, _data, "date");
            trySetProp(this, _data, "draft");
        }
    }

    static fromJS(data: any): WorkflowHistoryItem {
        data = typeof data === 'object' ? data : {};
        let result = new WorkflowHistoryItem();
        result.init(data);
        return result;
    }

    toJSON(data?: any) {
        data = typeof data === 'object' ? data : {};

        trySetProp(data, this, "previousState");
        trySetProp(data, this, "applicationState");
        trySetProp(data, this, "author");
        trySetProp(data, this, "date");
        trySetProp(data, this, "draft");

        return data;
    }
}
